import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatLegacyButton as MatButton } from "@angular/material/legacy-button";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { select, Store } from "@ngrx/store";
import { RenewPasswordContainer } from "@obg/common.login/renew-password/renew-password.container";
import { CustomerSupportService } from "@obg/core.customer-support";
import { GtmLoginMethod } from "@obg/gtm.types/adaptive";
import { AlertButtonColor, AlertButtonStyle, DialogService } from "@obg/mat.ui.core";
import { CommonState, TwoFactorAuthConfigService } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	CORE_ROUTE,
	ImageContent,
	Jurisdiction,
	JurisdictionPrefixPipe,
	JurisdictionSelector,
	MarketSelector,
	MessageService,
	MessageType,
	ProductType,
	SessionSelector,
	UiInteraction,
} from "@obg/ngx.core";
import { CoreContentState, ImageSelector, TranslationGroup } from "@obg/ngx.core.content";
import { DeviceConfigService, PlatformService } from "@obg/ngx.global";
import { coerceBoolean, TelemetryService } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { LotbaSelfExclusionAlertContainer } from "@obg/ngx.ui.common";
import { CoreUiConfigService, TrackingContext } from "@obg/ngx.ui.core";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, EMPTY, from, merge, Observable, Subscription } from "rxjs";
import { filter, map, take, tap, withLatestFrom } from "rxjs/operators";

import { LoginFeatureState } from "../../sdk";
import { SuspiciousLoginResponse } from "../../sdk/suspicious-login/suspicious-login.model";
import { LoginErrorService } from "../login-errors/login-error.service";
import { LoginFormService } from "../login-form.service";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginSupportInteractionEvent } from "../login-ui/login-ui.const";
import { SocialLoginService } from "../social-login/social.login.service";

import {
	EXCLUDED_GENERIC_ERROR_MESSAGE_CODES,
	EXCLUDED_TWO_FACTOR_AUTH_MESSAGE_CODES,
	LOGIN_SELF_EXCLUSION_ERROR_CODE,
	TWO_FACTOR_AUTH_USER_IS_LOCKED_TEXT,
	ACCOUNT_NOT_VERIFIED_ERROR_CODE,
} from "./generic-login.const";

export const LOGIN_ERROR_ALERT_ID = "LOGIN_ERROR_ALERT";

@Component({
	selector: "obg-m-generic-login-container",
	templateUrl: "./generic-login.container.html",
	styleUrls: ["./generic-login.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	providers: [LoginFormService],
})
export class GenericLoginContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-generic-login") hostCssClass = true;

	@Input()
	@coerceBoolean()
	allLinksExternal = false;

	@Input()
	@coerceBoolean()
	autoFocus = false;

	@Input()
	@coerceBoolean()
	errorsInAlert = false;

	@Input()
	@coerceBoolean()
	disallowedRedirection = false;

	@Input()
	@coerceBoolean()
	disallowLogin = false;

	@Output() busy = new EventEmitter<boolean>();
	@Output() forgotPasswordClick = new EventEmitter();

	@ViewChild("username", { static: true })
	set input(value: ElementRef<HTMLInputElement> | undefined) {
		if (!value || !this.platformService.isBrowser || !this.autoFocus) {
			return;
		}

		setTimeout(() => value.nativeElement.focus());
	}

	@ViewChild("submitButton", { read: MatButton }) set submitButton(matButton: MatButton) {
		if (!matButton || !this.platformService.isBrowser) {
			return;
		}
		this.submitButtonElement = matButton;
	}

	error: string | undefined;
	customerCanLogInAt: string | undefined;
	formGroup: UntypedFormGroup;
	forgotPasswordRoute = CORE_ROUTE.auth.forgotPassword;
	forgotPasswordTrackingData: UiInteraction = {
		context: TrackingContext.LoginSupportInteractionContext,
		event: LoginSupportInteractionEvent.ForgotPassword,
	};
	isUkgcLoginMessageVisible$: Observable<boolean | undefined> | undefined;
	isBankIdTextEnabled$: Observable<boolean> | undefined;

	isGoogleLoginEnabled$: Observable<boolean> | undefined;
	isRememberMeAllowed$: Observable<boolean> | undefined;
	errorImage$: Observable<ImageContent> | undefined;
	rememberMeJurisdictions = [Jurisdiction.Ksa, Jurisdiction.Dgoj];
	isErrorRelatedToTwoFactorAuth = false;
	isErrorRelatedToPepSanction = false;
	iconError: ImageContent | undefined;

	jurisdiction: Jurisdiction | undefined;

	private data$$: Subscription | undefined;
	private submitButtonElement: MatButton | undefined;

	constructor(
		private loginFormService: LoginFormService,
		private loginUiFlowService: LoginUiFlowService,
		private store: Store<LoginFeatureState & CommonState & CoreContentState>,
		private messageService: MessageService,
		private platformService: PlatformService,
		private alertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private changeDetector: ChangeDetectorRef,
		private marketSelector: MarketSelector,
		private coreUiConfigService: CoreUiConfigService,
		private socialLoginService: SocialLoginService,
		private telemetryService: TelemetryService,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe,
		private twoFactorAuthConfigService: TwoFactorAuthConfigService,
		private matDialog: MatDialog,
		private deviceConfigService: DeviceConfigService,
		private customerSupportService: CustomerSupportService,
		private dialogService: DialogService,
		private sessionSelector: SessionSelector,
		private loginErrorService: LoginErrorService,
		private imageSelector: ImageSelector
	) {
		this.formGroup = loginFormService.loginForm;
	}

	ngOnInit() {
		this.loginFormService.initialize();

		this.isGoogleLoginEnabled$ = from(this.socialLoginService.getIsGoogleLoginEnabled());
		this.isBankIdTextEnabled$ = !this.coreUiConfigService.getSgaCredentialsEnabled()
			? combineLatest([
					this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
					this.store.select(this.marketSelector.getLanguageCode()),
				]).pipe(
					map(([jurisdiction, languageCode]) => jurisdiction === Jurisdiction.Sga || languageCode === "sv")
				)
			: EMPTY;

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				this.jurisdiction = jurisdiction;
			})
		);

		const genericError$ = this.loginUiFlowService.genericLoginError$().pipe(
			filter((message) => {
				this.busy.emit(false);

				const excludedGenericCode = this.twoFactorAuthConfigService.getPostRegistrationOTPEnabled()
					? [...EXCLUDED_GENERIC_ERROR_MESSAGE_CODES, ACCOUNT_NOT_VERIFIED_ERROR_CODE]
					: EXCLUDED_GENERIC_ERROR_MESSAGE_CODES;
				const excludedCodes =
					this.twoFactorAuthConfigService.getTwoFactorAuthEnabled() ||
					this.twoFactorAuthConfigService.getSuspiciousLoginEnabled()
						? EXCLUDED_TWO_FACTOR_AUTH_MESSAGE_CODES
						: excludedGenericCode;
				const isExcludedGenericError = excludedCodes.includes(message[0].messageCode);
				const challengeDetails = (message[0].data as SuspiciousLoginResponse)?.challengeDetails;
				const isSuspiciousLogin =
					message[0].messageCode === AUTH_MESSAGE_CODES.login.customerNotAllowed && !!challengeDetails;
				return !isExcludedGenericError && !isSuspiciousLogin;
			}),
			withLatestFrom(this.store.select(JurisdictionSelector.get)),
			tap(([message, jurisdiction]) => {
				this.jurisdiction = jurisdiction;
				if (message[0].messageCode === LOGIN_SELF_EXCLUSION_ERROR_CODE && jurisdiction === Jurisdiction.Lotba) {
					this.alertService.open(
						{
							id: LOGIN_ERROR_ALERT_ID,
							mustBeInteracted: true,
							hideClose: true,
							messageComponent: LotbaSelfExclusionAlertContainer,
							messageComponentInit: (c: LotbaSelfExclusionAlertContainer) =>
								(c.data = {
									title: "self-exclusion.lotba-alert.title",
									info: "self-exclusion.lotba-alert.info",
								}),
							dialogConfig: {
								panelClass: "lotba-self-exclusion-wrapper",
							},
						},
						[]
					);
				}

				if (
					message[0].messageCode === AUTH_MESSAGE_CODES.login.customerSelfExcluded &&
					jurisdiction === Jurisdiction.Pega &&
					this.errorsInAlert
				) {
					return this.loginErrorService.openAlert();
				}

				if (message[0].messageCode === AUTH_MESSAGE_CODES.login.resetPasswordRequired) {
					this.dialogService
						.openComponent(RenewPasswordContainer, {
							disableClose: true,
							data: {
								selectedUserEmail: this.formGroup.controls.username.value,
							},
						})
						.afterClosed();
					return;
				}

				const errorMessageCode = navigator.onLine ? message[0].messageCode : AUTH_MESSAGE_CODES.login.error;
				this.displayLoginError(errorMessageCode || "login-error");
			})
		);

		const routeError$ = this.store.select(RouteSelector.getAdditionalData).pipe(
			filter((data) => data && data.length > 0 && data[0].messageCode),
			tap((message) => this.displayLoginError(message[0].messageCode || "login-error"))
		);

		const sessionLimitExpired$ = this.loginUiFlowService.sessionLimitExpired$().pipe(
			tap((error) => {
				this.busy.emit(false);
				this.customerCanLogInAt = error.customerCanLogInAt;
				this.error = `login.${error?.code}`.toLowerCase();
			})
		);

		const authErrorMessages$ = this.messageService
			.consume$({
				context: AUTH_MESSAGE_CONTEXT.login,
				messageType: MessageType.Error,
				messageCode: [
					AUTH_MESSAGE_CODES.login.invalidCredentials,
					AUTH_MESSAGE_CODES.login.disallowedErrorCode,
					AUTH_MESSAGE_CODES.login.geofencingMultipleTries,
					AUTH_MESSAGE_CODES.login.geofencingGeneralError,
					AUTH_MESSAGE_CODES.login.geofencingServerError,
				],
			})
			.pipe(
				filter((message) => !!message.length),
				tap((message) => {
					this.busy.emit(false);
					this.error = `login.${message[0].messageCode.toLowerCase()}`;
				})
			);

		const brandRedirection$ = this.store.select(this.sessionSelector.getIsRedirectBrand()).pipe(
			filter((isRedirectBrand) => !!isRedirectBrand),
			tap(() => {
				this.busy.emit(false);
			})
		);

		const socialLoginErrors$ = combineLatest([
			this.socialLoginService.isOidcLoginException$(),
			this.socialLoginService.isConnectedAccountMissing$(),
		]).pipe(
			tap(([isOidcLoginException, isConnectedAccountMissing]) => {
				if (isConnectedAccountMissing) {
					this.error = "login.missing-connected-oidc-account";
				} else if (isOidcLoginException) {
					this.error = "login.generic-oidc-error";
				}
			})
		);

		this.isUkgcLoginMessageVisible$ = combineLatest([
			this.store.select(JurisdictionSelector.get).pipe(ignoreNil()),
			this.store.select<{ product: ProductType } | undefined>(RouteSelector.getData),
		]).pipe(
			map(
				([jurisdiction, routeData]) =>
					jurisdiction === Jurisdiction.Ukgc &&
					routeData &&
					[ProductType.Casino, ProductType.LiveCasino].includes(routeData.product)
			)
		);

		this.isRememberMeAllowed$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			map((jurisdiction) => !this.rememberMeJurisdictions.includes(jurisdiction))
		);

		this.errorImage$ = this.store.pipe(
			select(this.imageSelector.getByKey("general", "general.error-icon")),
			ignoreNil(),
			map((imageContent) => (this.iconError = imageContent))
		);

		this.data$$ = merge(
			genericError$,
			routeError$,
			sessionLimitExpired$,
			socialLoginErrors$,
			brandRedirection$,
			jurisdiction$,
			authErrorMessages$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	login() {
		this.error = undefined;
		this.customerCanLogInAt = undefined;
		this.isErrorRelatedToTwoFactorAuth = false;

		if (this.submitButtonElement) {
			this.submitButtonElement.focus();
		}

		if (this.loginFormService.hasEmptyFields()) {
			this.error = `login.${AUTH_MESSAGE_CODES.login.invalidCredentials.toLowerCase()}`;
			return;
		}

		this.busy.emit(true);
		this.loginFormService.login(this.disallowedRedirection, this.disallowLogin);
	}

	displayLoginError(messageCode: string) {
		this.isErrorRelatedToTwoFactorAuth =
			messageCode === AUTH_MESSAGE_CODES.login.customerNotAllowed &&
			this.twoFactorAuthConfigService.getTwoFactorAuthEnabled();
		this.isErrorRelatedToPepSanction = messageCode === AUTH_MESSAGE_CODES.login.customerPepSanctioned;

		if (this.isErrorRelatedToTwoFactorAuth) {
			return (this.error = TWO_FACTOR_AUTH_USER_IS_LOCKED_TEXT);
		}
		if (this.isErrorRelatedToPepSanction) {
			return this.openBraSnackBar();
		}

		const error = `login.${messageCode}`.toLowerCase();
		if (!this.errorsInAlert) {
			return (this.error = error);
		}

		if (this.jurisdiction === Jurisdiction.Ksa) {
			return this.openKsaAlert(error);
		}

		if (this.jurisdiction === Jurisdiction.BRA) {
			return this.openBraAlert(error);
		}

		return this.openAlert(error);
	}

	customerSupportOnClick(shouldCloseDialog?: boolean) {
		this.customerSupportService.openSupportPageInNewTab();
		if (shouldCloseDialog) {
			this.deviceConfigService.isDesktop() && this.matDialog.closeAll();
		}
	}

	openAlert(error: string) {
		this.alertService.open(
			{
				variant: "alternate",
				verticalButtons: true,
				id: LOGIN_ERROR_ALERT_ID,
				title: this.jurisdictionPrefixPipe.transform("login.title.login-error"),
				message: this.jurisdictionPrefixPipe.transform(error),
				buttons: [
					{
						color: AlertButtonColor.Accent,
						style: AlertButtonStyle.Flat,
						text: "general.contact",
						handler: () => {
							this.customerSupportOnClick();
						},
					},
					{
						color: AlertButtonColor.Primary,
						style: AlertButtonStyle.Stroked,
						text: "general.close",
					},
				],
			},
			[]
		);
	}

	openBraAlert(error: string) {
		this.alertService.open(
			{
				variant: "alternate",
				verticalButtons: true,
				id: LOGIN_ERROR_ALERT_ID,
				title: this.jurisdictionPrefixPipe.transform("login.title.login-error"),
				message: this.jurisdictionPrefixPipe.transform(error),
				buttons: [
					{
						color: AlertButtonColor.Accent,
						style: AlertButtonStyle.Flat,
						text: "login.forgot-password",
						handler: () => {
							this.store.dispatch(
								RouteAction.navigate(this.forgotPasswordRoute, undefined, {
									state: { username: this.formGroup.value.username },
								})
							);
							this.forgotPasswordClick.emit();
						},
					},
					{
						color: AlertButtonColor.Primary,
						style: AlertButtonStyle.Stroked,
						text: "general.contact",
						handler: () => this.customerSupportService.openSupportPageInNewTab(),
					},
				],
			},
			[]
		);
	}

	openKsaAlert(error: string) {
		this.alertService.open(
			{
				id: LOGIN_ERROR_ALERT_ID,
				title: this.jurisdictionPrefixPipe.transform("login.title.login-error"),
				message: this.jurisdictionPrefixPipe.transform(error),
				buttons: [
					{
						text: "general.close",
					},
					{
						text: "general.contact",
						handler: () => this.customerSupportService.openSupportPageInNewTab(),
					},
				],
			},
			[]
		);
	}

	openBraSnackBar() {
		this.snackBarService
			.openSnackBar$(
				{
					dismissible: true,
					icon: this.iconError,
					message: "login.e_personal_details_sanctioned",
					duration: 30000,
					panelClass: ["snackbar-icons"],
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	gtmEventAction(fieldName: string) {
		this.telemetryService.track({
			event: GtmEventAction.LoginFieldChange,
			FieldName: fieldName,
			LoginMethod: GtmLoginMethod.Credentials,
		});
	}
}
